import React from "react";
import { Box } from "@mui/material";
import { parse } from "date-fns";
import { useSelector } from "react-redux";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { formatPrescriptionPeriod } from "./prescriptionPeriod";
import { ServiceContent } from "../../../new-reports/types/newReportTypes";
import { RootStateServiceLogicBuilder } from "../../store/service-logic-builder-slice";
import ServiceActiveCompoundsSection from "./ServiceActiveCompoundsSection";
import ServiceRationaleSection from "./ServiceRationaleSection";
import ServiceAttributesSection from "./ServiceAttributesSection";
import ServiceBenefitsSection from "./ServiceBenefitsSection";
import calendarIcon from "../../../new-reports/assets/calendarIcon.png";
import checkIconBlue from "../../../new-reports/assets/check-single-blue.png";
import checkIconGreen from "../../../new-reports/assets/check-single-green.png";

/**
 * One prescribed service, rendered the same way everywhere it is shown.
 *
 * Extracted from `programming/ServiceViewModal`, which was the original and
 * stays the reference: the History detail page had been built by copying this
 * body, and the two promptly drifted — different image heights, a different
 * benefit order, benefits that were clickable in one and inert in the other,
 * and a period separator that differed by a glyph. Now the modal is this
 * component in a `Dialog` and the page is this component in a card, so there
 * is one implementation to correct.
 *
 * It renders the service's *content* only — name, image, period, description
 * and the section stack. Everything around it belongs to the host: the dialog
 * and its close button, or the page's card and client badge (`headerRight`).
 *
 * The section sizes are fixed here rather than left to callers. They default
 * to 11px/9px for the `new-reports` PDF pages, which render at a fixed print
 * scale; every on-screen caller has to override them, and forgetting to is
 * exactly how this page shipped at report size.
 */

/** 14px section titles over 12px body — the on-screen sizes, per the design. */
const SECTION_TITLE_SIZE = "14px";
const SECTION_BODY_SIZE = "12px";

export interface ServiceContentLabels {
  prescriptionPeriod: string;
  activeCompounds: string;
  activeSubstancesFormula: string;
  activeSubstances: string;
  attributes: string;
  rational: string;
  conditions: string;
  pnoeBenefits: string;
  additionalBenefits: string;
  furtherDetails?: string;
}

interface ServiceContentViewProps {
  service: ServiceContent;
  labels: ServiceContentLabels;
  unitType: number;
  /**
   * Columns for the attributes grid. The modal is 700px wide and fits two; the
   * full-page host is roughly twice that and fits three.
   */
  attributeColumns?: number;
  /** Right of the name/type block — the page's client badge goes here. */
  headerRight?: React.ReactNode;
  /** Beside the service name — the History page's Prescription link. */
  titleAside?: React.ReactNode;
}

/**
 * Which "active compounds" heading applies: the label depends on the service
 * type, and the three variants read differently enough to matter.
 */
const activeCompoundLabel = (
  service: ServiceContent,
  labels: ServiceContentLabels
) => {
  if (service.serviceTypeCode === 3) return labels.activeSubstancesFormula;
  if (service.serviceTypeCode === 4 || service.serviceTypeCode === 5) {
    return labels.activeSubstances;
  }
  return labels.activeCompounds;
};

const ServiceContentView: React.FC<ServiceContentViewProps> = ({
  service,
  labels,
  unitType,
  attributeColumns = 2,
  headerRight,
  titleAside,
}) => {
  const theme: Theme = useTheme();

  // Field-label formatting for the rationale's conditions.
  const categoryOptions = useSelector(
    (state: RootStateServiceLogicBuilder) =>
      state.serviceLogicBuilder.categoryOptions
  );

  const rules = service.rational?.list?.[0];
  const period = service.prescriptionPeriod;
  // Both the report payload and the history fixture use `dd-MM-yyyy`.
  const parsePeriodDate = (value: string) =>
    parse(value, "dd-MM-yyyy", new Date());

  return (
    <>
      <Box sx={styles.header}>
        <Box sx={styles.headerText}>
          <Box sx={styles.titleRow}>
            <Box component="p" sx={styles.serviceName(theme)}>
              {service.serviceName}
            </Box>
            {titleAside}
          </Box>
          <Box component="p" sx={styles.serviceType(theme)}>
            {service.serviceType}
          </Box>
        </Box>
        {headerRight}
      </Box>

      {service.imageURL && (
        <Box sx={styles.imageContainer}>
          <Box
            component="img"
            src={service.imageURL}
            alt={service.serviceName}
            sx={styles.image}
          />
        </Box>
      )}

      {period?.startDate && (
        <Box sx={styles.prescriptionPeriod(theme)}>
          <Box component="p" sx={styles.periodLabel(theme)}>
            <Box
              component="img"
              src={calendarIcon}
              alt="calendar"
              sx={styles.calendarIcon}
            />
            {labels.prescriptionPeriod}
          </Box>
          <Box component="p" sx={styles.periodValue(theme)}>
            {formatPrescriptionPeriod({
              startDate: parsePeriodDate(period.startDate),
              endDate: period.endDate
                ? parsePeriodDate(period.endDate)
                : undefined,
              deactivationDate: service.deactivationDate,
            })}
          </Box>
        </Box>
      )}

      {service.description && (
        <Box component="p" sx={styles.description(theme)}>
          {service.description}
        </Box>
      )}

      <ServiceActiveCompoundsSection
        activeCompounds={service.activeCompounds}
        label={activeCompoundLabel(service, labels)}
        fontSizes={{
          sectionTitle: SECTION_TITLE_SIZE,
          compoundText: SECTION_BODY_SIZE,
        }}
      />

      <ServiceRationaleSection
        rules={rules}
        labels={{ rational: labels.rational, conditions: labels.conditions }}
        categoryOptions={categoryOptions}
        unitType={unitType}
        fontSizes={{
          sectionTitle: SECTION_TITLE_SIZE,
          rationalHeader: SECTION_BODY_SIZE,
          conditionCategory: SECTION_BODY_SIZE,
          conditionItem: SECTION_BODY_SIZE,
        }}
      />

      <ServiceAttributesSection
        attributes={service.attributes}
        label={labels.attributes}
        gridColumns={attributeColumns}
        fontSizes={{
          sectionTitle: SECTION_TITLE_SIZE,
          attributeLabel: SECTION_BODY_SIZE,
          attributeValue: SECTION_BODY_SIZE,
        }}
      />

      <ServiceBenefitsSection
        benefits={service.pnoeBenefits}
        label={labels.pnoeBenefits}
        checkIconSrc={checkIconBlue}
        backgroundColor={theme.colorTokens.P[10]}
        gridColumns={2}
        clickable={true}
        fontSizes={{
          sectionTitle: SECTION_TITLE_SIZE,
          benefitText: SECTION_BODY_SIZE,
        }}
      />

      <ServiceBenefitsSection
        benefits={service.additionalBenefits}
        label={labels.additionalBenefits}
        checkIconSrc={checkIconGreen}
        gridColumns={2}
        clickable={true}
        fontSizes={{
          sectionTitle: SECTION_TITLE_SIZE,
          benefitText: SECTION_BODY_SIZE,
        }}
      />

      {service.details.some((detail) => detail.text !== null) && (
        <Box sx={styles.section}>
          <Box component="p" sx={styles.sectionTitle(theme)}>
            {labels.furtherDetails ?? "Further details"}
          </Box>
          <Box sx={styles.furtherDetailsContainer(theme)}>
            {service.details
              .filter((detail) => detail.text !== null)
              .map((detail) => (
                <Box key={detail.name} sx={styles.detailItem}>
                  <Box component="p" sx={styles.detailLabel(theme)}>
                    {detail.name}
                  </Box>
                  <Box component="p" sx={styles.detailText(theme)}>
                    {detail.text}
                  </Box>
                </Box>
              ))}
          </Box>
        </Box>
      )}
    </>
  );
};

export default ServiceContentView;

const styles = {
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "24px",
  },
  headerText: {
    minWidth: 0,
  },
  // The name keeps its own bottom margin; the row adds it back so an aside
  // centres on the name, not on name + margin.
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
    marginBottom: "4px",
    "& > p": { marginBottom: 0 },
  },
  // 20px over 14px Regular, per Figma node 16611:104699 — the modal had 24px
  // over 14px Light, which no design node backs.
  serviceName: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "20px",
    lineHeight: "1.4",
    color: theme.colorTokens.N[90],
    margin: 0,
    marginBottom: "4px",
  }),
  serviceType: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "1.6",
    color: theme.colorTokens.N[80],
    margin: 0,
  }),
  imageContainer: {
    width: "100%",
    height: "150px",
    borderRadius: "8px",
    overflow: "hidden",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as "cover",
  },
  prescriptionPeriod: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "4px",
    borderRadius: "8px",
    backgroundColor: theme.colorTokens.N[30],
    padding: "8px",
  }),
  periodLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
    color: theme.colorTokens.N[80],
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: "8px",
  }),
  periodValue: (theme: Theme) => ({
    fontFamily: "var(--font-Light)",
    fontSize: "12px",
    lineHeight: "20px",
    color: theme.colorTokens.N[90],
    margin: 0,
  }),
  calendarIcon: {
    width: "16px",
    height: "16px",
    objectFit: "contain" as "contain",
  },
  description: (theme: Theme) => ({
    fontFamily: "var(--font-Light)",
    fontSize: "12px",
    lineHeight: "18px",
    color: theme.colorTokens.N[90],
    margin: 0,
  }),
  section: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "12px",
  },
  sectionTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: SECTION_TITLE_SIZE,
    color: theme.colorTokens.N[90],
    margin: 0,
  }),
  furtherDetailsContainer: (theme: Theme) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "12px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "12px",
  }),
  detailItem: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
  },
  detailLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "14px",
    color: theme.colorTokens.N[80],
    margin: 0,
  }),
  detailText: (theme: Theme) => ({
    fontFamily: "var(--font-Light)",
    fontSize: "12px",
    lineHeight: "18px",
    color: theme.colorTokens.N[90],
    margin: 0,
    whiteSpace: "pre-wrap" as "pre-wrap",
  }),
};
